import { Flex } from 'components/Flex';
import {
  GreyText,
  InnerHeaderWrapper,
  MainTitle,
  NavigationButton,
  NavigationButtonsWrapper,
  AgencyAccept,
  ShiftStatusText,
  ShiftStatusTextWrapper,
  Text,
  ShiftStatusDot,
  CommunityName,
} from 'modals/ShiftModal/src/views/DetailView/DetailView.styled';
import { ShiftJaneSection } from 'modals/ShiftModal/src/views/DetailView/DetailTabView/DetailTabView.styled';
import {
  DetailViewActiveTabType,
  DetailViewComponentMap,
  IDetailViewComponentProps,
} from 'modals/ShiftModal/src/views/DetailView/DetailView.types';
import { FC, useState } from 'react';
import { css } from 'styled-components';
import { useTranslation } from 'hooks/useTranslation';
import { Tags } from 'components/Tags';
import { getDefaultDetailViewTab, isTimesheetsTabAvailable } from '../DetailView.constants';
import { OrderStatusEnum, OrderStatus } from 'types/graphql';
import { ShiftModalDetailsData } from 'modals/ShiftModal/ShiftModal.constants';
import {
  getShiftModalShiftDate,
  getShiftModalShiftTimeRange,
  getShiftModalShiftTotalDuration,
} from 'mappers/shiftModalOrderDetailsMapper';
import { useSelector } from 'react-redux';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import isUserTimeFormat24hSelector from 'store/selectors/userSelectors/isUserTimeFormat24hSelector';
import { SessionService } from 'services';
import { usePathname } from 'hooks/usePathname';
import { CanAssignValidationProvider } from 'modals/ShiftModal/src/views/DetailView/ConfirmAvailabilityModal/CanAssignValidation.validation.context';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { DetailViewFooter } from 'modals/ShiftModal/src/views/DetailView/DetailViewFooter';

export function DetailViewInnerHeader({ data }: { data: ShiftModalDetailsData }) {
  const order = data?.order;
  const { t, i18n } = useTranslation();
  const isAcceptedByAgency = !!order?.orderSchedule.mainOrderJane?.assignedBy?.displayId;
  const isUserTimeFormat24h = useSelector(isUserTimeFormat24hSelector);

  const isUserAgency = useSelector(isUserAgencySelector);

  if (!order) return null;

  return (
    <InnerHeaderWrapper>
      <MainTitle>{order.position.name}</MainTitle>
      {(isUserAgency || !!order.isExternal) && (
        <CommunityName>{order.community.name}</CommunityName>
      )}
      <ShiftStatusTextWrapper>
        <ShiftStatusDot type={order.status.name.toLowerCase()} isBlockShift={order.isBlockShift}>
          •
        </ShiftStatusDot>
        <ShiftStatusText>
          {getTranslatedResponse(order.status.name, 'status', i18n, t)}
          {!!isAcceptedByAgency && <AgencyAccept>&nbsp;(by Agency Admin)</AgencyAccept>}
        </ShiftStatusText>
      </ShiftStatusTextWrapper>
      {!!order.mainJane && <ShiftJaneSection data={data} />}
      <Flex>
        <Text>{getShiftModalShiftDate(order)}</Text>
      </Flex>
      <Flex>
        <Text>{getShiftModalShiftTimeRange(order, isUserTimeFormat24h)}</Text>
        &nbsp;
        <GreyText>({getShiftModalShiftTotalDuration(order)})</GreyText>
      </Flex>
      <Flex margin={`0 0 ${order.noShow && '12px'} 0`}>
        <Tags
          id={order.orderSchedule.id}
          status={order.status.value}
          isRecurring={!!order.recurrence}
          sentToAgencies={order.sentToAgencies}
          isExternal={order.isExternal}
          isBlockShift={order.isBlockShift}
          janePrimaryCommunity={order.mainJane?.primaryCommunity}
          isAcceptedBySharedTeamMember={order.acceptedBySharedTeamMember!}
          biddable={order.biddable}
          biddingTimeEndsAt={order.biddingTimeEndsAt}
          awardingTimeStartsAt={order.awardingTimeStartsAt}
          bids={order.bids}
          timeZone={order.timeZone?.toString()}
        />
      </Flex>
    </InnerHeaderWrapper>
  );
}

const tabs: [string, DetailViewActiveTabType][] = [
  ['General', 'GeneralTab'],
  ['Activity', 'ActivityTab'],
  ['Available Members', 'AvailableMembersTab'],
  ['Timesheets', 'TimesheetsTab'],
];

const orderStatusesForAgencyTeamMembersTab = [
  OrderStatusEnum.accepted,
  OrderStatusEnum.completed,
  OrderStatusEnum.pending,
  OrderStatusEnum.unfulfilled,
];

const orderStatusesForB2BTeamMembersTab = [
  OrderStatusEnum.accepted,
  OrderStatusEnum.cancelled,
  OrderStatusEnum.completed,
  OrderStatusEnum.in_progress,
  OrderStatusEnum.pending,
  OrderStatusEnum.requested,
  OrderStatusEnum.unfulfilled,
  OrderStatusEnum.absent,
];

const shouldShowAvailableMembers = (isUserAgency: boolean, orderStatus: OrderStatus) => {
  if (isUserAgency) return orderStatusesForAgencyTeamMembersTab.includes(orderStatus.value);

  return orderStatusesForB2BTeamMembersTab.includes(orderStatus.value);
};

export const DetailTabView: FC<IDetailViewComponentProps> = ({ data }) => {
  const order = data?.order;
  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState<DetailViewActiveTabType>(() =>
    getDefaultDetailViewTab(pathname, order),
  );
  const { t, i18n } = useTranslation();
  const ActiveTabComponent = DetailViewComponentMap[activeTab];
  const isUserAgency = useSelector(isUserAgencySelector);

  const timeZone = order?.timeZone?.toString();

  if (!order) return null;

  return (
    <CanAssignValidationProvider>
      <Flex
        flexDirection="column"
        height="fit-content"
        minHeight="fit-content"
        css={css`
          position: sticky;
          background-color: white;
          z-index: 1;
          top: 0;
        `}
      >
        <NavigationButtonsWrapper>
          {tabs
            .filter(([, key]) => {
              if (key === 'AvailableMembersTab') {
                return shouldShowAvailableMembers(isUserAgency, order.status);
              } else if (key === 'TimesheetsTab') {
                return isTimesheetsTabAvailable(order);
              }
              return true;
            })
            .map(([label, _enum]) => {
              return (
                <NavigationButton
                  key={label}
                  isDisabled={
                    !!(
                      label === 'Activity' &&
                      ((!!order.isAway && !order.sentToAgencies) || !!order.isExternal)
                    )
                  }
                  onClick={() => activeTab !== _enum && setActiveTab(_enum)}
                  isActive={activeTab === _enum}
                >
                  {SessionService.assertUserRole('Premium') && _enum === 'AvailableMembersTab'
                    ? t('shift_modal:AUDIT_TRAIL')
                    : getTranslatedResponse(label, 'shift_modal', i18n, t)}
                </NavigationButton>
              );
            })}
        </NavigationButtonsWrapper>
      </Flex>

      <ActiveTabComponent timeZone={timeZone} data={data} />

      {activeTab !== 'TimesheetsTab' && (
        <BKJSideModalFooter>
          <DetailViewFooter />
        </BKJSideModalFooter>
      )}
    </CanAssignValidationProvider>
  );
};
